import { compact } from 'lodash';
import type { Moment } from 'moment';
import type moment from 'moment';
import type { ReactNode } from 'react';
import React, { useCallback, useContext, useMemo } from 'react';
import Timeline, {
  DateHeader,
  TimelineHeaders,
  TimelineMarkers,
  TodayMarker,
} from './react-calendar-timeline';
import type {
  IntervalRenderer,
  ReactCalendarTimelineProps,
  TimelineGroupBase,
  TimelineItemBase,
} from './react-calendar-timeline';
import { Button } from '../ui';
import { IconWrapper } from '../Icons';
import type { EventTimeline } from '../core/Timelines';
import type { Erd } from 'element-resize-detector';
import type { CustomDateHeaderPropsData } from './react-calendar-timeline/lib/headers/CustomDateHeader';
import TimelinePreferencesContext from '../contexts/TimelinePreferencesContext';

const dateRenderer = (timeStart: moment.Moment, labelWidth: number) => {
  if (labelWidth < 20) return '';
  else if (labelWidth < 40) return timeStart.format('D');
  else if (labelWidth < 75) return timeStart.format('D.M.');
  return timeStart.format('ddd D.M.');
};

export const timelineTimeSteps = {
  second: 1,
  minute: 1,
  hour: 3,
  day: 1,
  week: 1,
  isoWeek: 1,
  month: 1,
  year: 1,
};

export interface ITimelineView<
  CustomItem extends TimelineItemBase,
  CustomGroup extends TimelineGroupBase,
> extends Timeline<CustomItem, CustomGroup> {
  _erdWidth: number | undefined;
  _erd: Erd | undefined;
}
import ZoomOutIconSvg from '../../images/zoom-out.svg';
import ZoomInIconSvg from '../../images/zoom-in.svg';
import LeftArrowIconSvg from '../../images/arrow-left.svg';
import RightArrowIconSvg from '../../images/arrow-right.svg';

// Minimum zoom of 1 week, maximum of 52 weeks (1 year)
const MIN_ZOOM_WEEKS = 1;
const MAX_ZOOM_WEEKS = 52;
const ZOOM_STEP = 2; // Number of weeks to zoom in/out by

export const TimelineControlButtons = ({
  isOperationsMobileView,
}: {
  isOperationsMobileView?: boolean;
}) => {
  const { currentZoomLevel, stepScroll, setZoomLevel } = useContext(
    TimelinePreferencesContext,
  );

  const handleZoomChange = (zoomIn: boolean) => {
    const newZoomLevel = Math.min(
      Math.max(
        currentZoomLevel + (zoomIn ? -ZOOM_STEP : ZOOM_STEP),
        MIN_ZOOM_WEEKS,
      ),
      MAX_ZOOM_WEEKS,
    );

    setZoomLevel(newZoomLevel);
  };

  return (
    <>
      <Button
        onPress={() => handleZoomChange(false)}
        mx={2}
        disabled={currentZoomLevel >= MAX_ZOOM_WEEKS}
        aria-label="zoom-out"
      >
        <IconWrapper>
          <img src={ZoomOutIconSvg} width={14} height={18} />
        </IconWrapper>
      </Button>
      <Button
        disabled={currentZoomLevel <= MIN_ZOOM_WEEKS}
        mx={2}
        onPress={() => handleZoomChange(true)}
        aria-label="zoom-in"
      >
        <IconWrapper>
          <img src={ZoomInIconSvg} width={14} height={18} />
        </IconWrapper>
      </Button>
      {!isOperationsMobileView && (
        <>
          <Button
            onPress={() => stepScroll(false)}
            mx="2"
            aria-label="left arrow"
          >
            <IconWrapper>
              <img src={LeftArrowIconSvg} width={14} height={18} />
            </IconWrapper>
          </Button>
          <Button onPress={() => stepScroll(true)} mx={2}>
            <IconWrapper>
              <img
                src={RightArrowIconSvg}
                width={14}
                height={18}
                aria-label="right arrow"
              />
            </IconWrapper>
          </Button>
        </>
      )}
    </>
  );
};

export const sortTimelineGroups = <
  T extends {
    timeline: EventTimeline;
  },
>(
  list: T[],
) => {
  let partials: T[] = [];

  return list.reduce((acc: T[], group, i: number) => {
    if (!group.timeline.canCollapse) partials.push(group);
    if (group.timeline.canCollapse || i >= list.length - 1) {
      partials.sort((a, b) => {
        const sortIdA = Number(a.timeline.vessel?.sortId ?? 0);
        const sortIdB = Number(b.timeline.vessel?.sortId ?? 0);
        const volCapA = Number(a.timeline.vessel?.volumeCapacity ?? 0);
        const volCapB = Number(b.timeline.vessel?.volumeCapacity ?? 0);
        const sortIdCompare = sortIdB - sortIdA;
        const volCapCompare = volCapB - volCapA;
        return sortIdCompare || volCapCompare;
      });

      acc.push(...partials);
      partials = [];
      if (group.timeline.canCollapse) {
        acc.push(group);
      }
    }

    return acc;
  }, []);
};

const TimelineView = <
  CustomItem extends TimelineItemBase,
  CustomGroup extends TimelineGroupBase,
>(
  props: ReactCalendarTimelineProps<CustomItem, CustomGroup> & {
    timelineRef: React.RefObject<ITimelineView<CustomItem, CustomGroup>>;
    extraHeaders?: JSX.Element;
    headerHeight?: number;
    children?: ReactNode;
    sidebarWidth?: number;
  },
): JSX.Element => {
  const { timelineRef, isReadOnlyScenario, sidebarWidth } = props;
  const { visibleTimeStart, visibleTimeEnd, setVisibleTime } = useContext(
    TimelinePreferencesContext,
  );

  const handleTimeChange = useCallback(
    (
      start: number,
      end: number,
      updateScrollCanvas: (start: number, end: number) => void,
    ) => {
      setVisibleTime(start, end);
      updateScrollCanvas(start, end);
    },
    [setVisibleTime],
  );

  return (
    <Timeline
      ref={timelineRef}
      minScale={0.5}
      maxScale={2}
      timeSteps={timelineTimeSteps}
      lineHeight={52}
      sidebarWidth={sidebarWidth}
      itemHeightRatio={1}
      canResize={false}
      {...props}
      visibleTimeStart={visibleTimeStart}
      visibleTimeEnd={visibleTimeEnd}
      onTimeChange={handleTimeChange}
      isReadOnlyScenario={isReadOnlyScenario}
    >
      <TimelineHeaders
        sticky
        calendarHeaderStyle={{
          borderBottom: 'none',
        }}
      >
        <DateHeader
          height={18}
          unit="month"
          labelFormat="MMMM YYYY"
          className="year-month-header"
        />
        <DateHeader height={18} unit="isoWeek" labelFormat="W" />
        <DateHeader
          height={20}
          unit="day"
          className="date-time-header"
          labelFormat={([timeStart], _unit, labelWidth) =>
            dateRenderer(timeStart, labelWidth)
          }
          intervalRenderer={CustomDateRenderer}
        />
        {Array.isArray(props.children)
          ? compact(props.children)
          : props.children}
      </TimelineHeaders>
      <TimelineMarkers>
        <TodayMarker date={Date.now()}>
          {({ styles }) => {
            return (
              <div
                id="today_marker"
                style={{ ...styles, backgroundColor: '#394353' }}
              />
            );
          }}
        </TodayMarker>
      </TimelineMarkers>
    </Timeline>
  );
};

function getDateTestId(timestamp: number | Moment) {
  const startDate = new Date(timestamp.valueOf());
  const startYear = startDate.getUTCFullYear();
  const startMonth = (startDate.getUTCMonth() + 1).toString().padStart(2, '0');
  const startDay = (startDate.getUTCDate() + 1).toString().padStart(2, '0');

  const testId = `timeline-date-${startYear}-${startMonth}-${startDay}`;

  return testId;
}

/** */
function CustomDateRenderer({
  getIntervalProps,
  intervalContext,
}: IntervalRenderer<CustomDateHeaderPropsData>): React.JSX.Element {
  const testId = useMemo(
    () => getDateTestId(intervalContext.interval.startTime),
    [intervalContext.interval.startTime],
  );

  return (
    <div
      {...getIntervalProps({})}
      className={`rct-dateHeader`}
      data-testid={testId}
    >
      <span>{intervalContext.intervalText}</span>
    </div>
  );
}

export default TimelineView;
